import { useEffect } from 'react';
import { useFestivalDays, useArtists, useSettings } from '../lib/usePublicData';

const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || 'https://zottezomerronse.be';

const ADDRESSES = {
    'kleine-markt-11-07': {
        streetAddress: 'Kleine Markt',
        postalCode: '9600',
        addressLocality: 'Ronse',
        addressCountry: 'BE',
    },
    'stadstuin-25-07': {
        streetAddress: 'Stadstuin',
        postalCode: '9600',
        addressLocality: 'Ronse',
        addressCountry: 'BE',
    },
    'grote-markt-29-08': {
        streetAddress: 'Grote Markt',
        postalCode: '9600',
        addressLocality: 'Ronse',
        addressCountry: 'BE',
    },
};

// Static start/end times derived from the published time_label (Brussels = +02:00 in summer).
const TIMES = {
    'kleine-markt-11-07': { start: '2026-07-11T18:00:00+02:00', end: '2026-07-12T01:00:00+02:00' },
    'stadstuin-25-07':    { start: '2026-07-25T16:00:00+02:00', end: '2026-07-25T23:00:00+02:00' },
    'grote-markt-29-08':  { start: '2026-08-29T16:00:00+02:00', end: '2026-08-30T01:00:00+02:00' },
};

const ABS = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${PUBLIC_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

const SCRIPT_ID = 'zz-event-jsonld';

export default function EventJsonLd() {
    const { data: days } = useFestivalDays();
    const { data: artists } = useArtists();
    const { data: settings } = useSettings();

    useEffect(() => {
        if (!days || days.length === 0) return;

        const ogImage = ABS(settings?.og_image_url || '/assets/og-image.jpg');
        const description = settings?.seo_description ||
            settings?.hero_intro ||
            'Gratis festival in Ronse met muziek, beleving en familieplezier op 11 juli, 25 juli en 29 augustus 2026.';

        const events = days
            .filter((d) => (d.active === undefined ? true : d.active))
            .map((d) => {
                const time = TIMES[d.id] || {};
                const performers = (artists || [])
                    .filter((a) => (a.festival_day_id || a.dayId) === d.id && (a.active === undefined ? true : a.active))
                    .map((a) => ({
                        '@type': 'PerformingGroup',
                        name: a.name,
                    }));

                return {
                    '@context': 'https://schema.org',
                    '@type': 'MusicEvent',
                    name: `Zotte Zomer Ronse 2026 — ${d.location}`,
                    startDate: time.start || `${d.date}T16:00:00+02:00`,
                    endDate: time.end || `${d.date}T23:00:00+02:00`,
                    eventStatus: 'https://schema.org/EventScheduled',
                    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
                    location: {
                        '@type': 'Place',
                        name: `${d.location} Ronse`,
                        address: {
                            '@type': 'PostalAddress',
                            ...(ADDRESSES[d.id] || {
                                streetAddress: d.location,
                                postalCode: '9600',
                                addressLocality: 'Ronse',
                                addressCountry: 'BE',
                            }),
                        },
                    },
                    organizer: [
                        {
                            '@type': 'GovernmentOrganization',
                            name: 'Stad Ronse',
                            url: 'https://www.ronse.be',
                        },
                        {
                            '@type': 'Organization',
                            name: 'Bright Events',
                            url: 'https://www.brightevents.be',
                        },
                    ],
                    performer: performers,
                    offers: {
                        '@type': 'Offer',
                        price: '0',
                        priceCurrency: 'EUR',
                        availability: 'https://schema.org/InStock',
                        url: `${PUBLIC_URL}/`,
                        validFrom: '2026-01-01T00:00:00+01:00',
                    },
                    image: [ogImage],
                    description: `${description} — ${d.display_date || d.date} · ${d.location} Ronse.`,
                };
            });

        if (events.length === 0) return;

        // Site-level Festival schema — distinct from the per-day Event records.
        // Helps Google show a unified "Zotte Zomer Ronse 2026" knowledge panel.
        const fb = (settings?.social_facebook_url || '').trim();
        const ig = (settings?.social_instagram_url || '').trim();
        const sameAs = [fb, ig].filter(Boolean);
        const festival = {
            '@context': 'https://schema.org',
            '@type': 'Festival',
            name: 'Zotte Zomer Ronse 2026',
            url: `${PUBLIC_URL}/`,
            logo: ABS(settings?.organizer_logo_url || '/assets/logos/stad-ronse.svg'),
            image: ABS(settings?.og_image_url || '/assets/og-image.jpg'),
            startDate: '2026-07-11',
            endDate: '2026-08-29',
            description: 'Gratis zomerfestival in Ronse (Vlaamse Ardennen, Oost-Vlaanderen, 9600). Drie zomerse festivaldagen op de Kleine Markt, in de Stadstuin en op de Grote Markt.',
            location: { '@type': 'City', name: 'Ronse', address: { '@type': 'PostalAddress', addressLocality: 'Ronse', postalCode: '9600', addressCountry: 'BE', addressRegion: 'Oost-Vlaanderen' } },
            organizer: [
                { '@type': 'GovernmentOrganization', name: 'Stad Ronse', url: 'https://www.ronse.be' },
                { '@type': 'Organization', name: 'Bright Events', url: 'https://www.brightevents.be' },
            ],
            ...(sameAs.length > 0 ? { sameAs } : {}),
        };

        // Inject (or replace) the JSON-LD script tag in <head>
        let scriptEl = document.getElementById(SCRIPT_ID);
        if (!scriptEl) {
            scriptEl = document.createElement('script');
            scriptEl.id = SCRIPT_ID;
            scriptEl.type = 'application/ld+json';
            document.head.appendChild(scriptEl);
        }
        scriptEl.text = JSON.stringify([festival, ...events]);

        return () => {
            // keep the tag — it's helpful for crawlers visiting the cached HTML
        };
    }, [days, artists, settings]);

    return null;
}
